import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  WebsiteVisitor,
  VisitorEvent,
  VisitorConversion,
  ConversionFunnel,
  WebsiteAnalyticsSummary,
} from '@/models/website-analytics.model';

const VISITORS_COLLECTION = 'websiteVisitors';
const VISITOR_EVENTS_COLLECTION = 'visitorEvents';
const CONVERSIONS_COLLECTION = 'visitorConversions';
const FUNNELS_COLLECTION = 'conversionFunnels';

export class WebsiteAnalyticsService {
  // ========== Visitor Tracking ==========

  static async trackVisitor(visitorData: Partial<WebsiteVisitor>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      sessionId: visitorData.sessionId || this.generateSessionId(),
      userId: visitorData.userId || null,
      isNewVisitor: visitorData.isNewVisitor ?? true,
      isReturning: visitorData.isReturning ?? false,
      
      deviceType: visitorData.deviceType || 'desktop',
      browser: visitorData.browser || 'unknown',
      os: visitorData.os || 'unknown',
      screenResolution: visitorData.screenResolution || null,
      language: visitorData.language || 'en',
      
      country: visitorData.country || null,
      region: visitorData.region || null,
      city: visitorData.city || null,
      
      referrer: visitorData.referrer || null,
      source: visitorData.source || 'direct',
      medium: visitorData.medium || null,
      campaign: visitorData.campaign || null,
      term: visitorData.term || null,
      adGroup: visitorData.adGroup || null,
      keyword: visitorData.keyword || null,
      
      utmSource: visitorData.utmSource || null,
      utmMedium: visitorData.utmMedium || null,
      utmCampaign: visitorData.utmCampaign || null,
      utmTerm: visitorData.utmTerm || null,
      utmContent: visitorData.utmContent || null,
      
      gclid: visitorData.gclid || null,
      gclsrc: visitorData.gclsrc || null,
      
      entryPage: visitorData.entryPage || '/',
      exitPage: visitorData.exitPage || null,
      pagesViewed: visitorData.pagesViewed || [visitorData.entryPage || '/'],
      timeOnSite: visitorData.timeOnSite || 0,
      sessionStartTime: visitorData.sessionStartTime ? Timestamp.fromDate(visitorData.sessionStartTime) : now,
      sessionEndTime: visitorData.sessionEndTime ? Timestamp.fromDate(visitorData.sessionEndTime) : null,
      isActive: visitorData.isActive ?? true,
      
      scrollDepth: visitorData.scrollDepth || null,
      eventsTriggered: visitorData.eventsTriggered || [],
      
      isPotentialEmployer: visitorData.isPotentialEmployer ?? false,
      isPotentialProvider: visitorData.isPotentialProvider ?? false,
      isPotentialEmployee: visitorData.isPotentialEmployee ?? false,
      interestLevel: visitorData.interestLevel || null,
      interestSignals: visitorData.interestSignals || [],
      
      convertedAt: visitorData.convertedAt ? Timestamp.fromDate(visitorData.convertedAt) : null,
      conversionType: visitorData.conversionType || null,
      convertedValue: visitorData.convertedValue || null,
      
      createdAt: now,
      lastSeen: now,
    };

    const docRef = await addDoc(collection(db, VISITORS_COLLECTION), data);
    return docRef.id;
  }

  static async updateVisitor(id: string, updates: Partial<WebsiteVisitor>): Promise<void> {
    const docRef = doc(db, VISITORS_COLLECTION, id);
    const updateData: any = {
      lastSeen: Timestamp.now(),
    };

    if (updates.exitPage !== undefined) updateData.exitPage = updates.exitPage;
    if (updates.pagesViewed !== undefined) updateData.pagesViewed = updates.pagesViewed;
    if (updates.timeOnSite !== undefined) updateData.timeOnSite = updates.timeOnSite;
    if (updates.sessionEndTime !== undefined) updateData.sessionEndTime = updates.sessionEndTime ? Timestamp.fromDate(updates.sessionEndTime) : null;
    if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
    if (updates.scrollDepth !== undefined) updateData.scrollDepth = updates.scrollDepth;
    if (updates.interestLevel !== undefined) updateData.interestLevel = updates.interestLevel;
    if (updates.interestSignals !== undefined) updateData.interestSignals = updates.interestSignals;
    if (updates.convertedAt !== undefined) updateData.convertedAt = updates.convertedAt ? Timestamp.fromDate(updates.convertedAt) : null;
    if (updates.conversionType !== undefined) updateData.conversionType = updates.conversionType;
    if (updates.convertedValue !== undefined) updateData.convertedValue = updates.convertedValue;

    await updateDoc(docRef, updateData);
  }

  static async getVisitorById(id: string): Promise<WebsiteVisitor | null> {
    const docRef = doc(db, VISITORS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToVisitor(docSnap.id, docSnap.data());
  }

  static async getVisitorsBySession(sessionId: string): Promise<WebsiteVisitor[]> {
    const q = query(
      collection(db, VISITORS_COLLECTION),
      where('sessionId', '==', sessionId),
      orderBy('sessionStartTime', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToVisitor(doc.id, doc.data()));
  }

  static async getRecentVisitors(limitCount: number = 50): Promise<WebsiteVisitor[]> {
    try {
      console.log('[WebsiteAnalyticsService] Fetching recent visitors:', { limit: limitCount });
      
      const q = query(
        collection(db, VISITORS_COLLECTION),
        orderBy('lastSeen', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      const visitors = snapshot.docs.map(doc => this.docToVisitor(doc.id, doc.data()));
      
      console.log('[WebsiteAnalyticsService] Retrieved recent visitors:', {
        count: visitors.length,
        visitorIds: visitors.map(v => v.id).slice(0, 5),
      });
      
      return visitors;
    } catch (error: any) {
      console.error('[WebsiteAnalyticsService] Error fetching recent visitors:', error);
      // If index error, try to get all and sort in memory (not recommended for large datasets)
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.warn('[WebsiteAnalyticsService] Index missing, fetching all visitors...');
        const q = query(collection(db, VISITORS_COLLECTION));
        const snapshot = await getDocs(q);
        const visitors = snapshot.docs.map(doc => this.docToVisitor(doc.id, doc.data()));
        return visitors
          .sort((a, b) => b.lastSeen.getTime() - a.lastSeen.getTime())
          .slice(0, limitCount);
      }
      throw error;
    }
  }

  static async getVisitorsInDateRange(startDate: Date, endDate: Date): Promise<WebsiteVisitor[]> {
    try {
      // Note: Firestore requires an index for range queries with orderBy
      // The index is defined in firestore.indexes.json
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      console.log('[WebsiteAnalyticsService] Fetching visitors in date range:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startTimestamp: startTimestamp.toMillis(),
        endTimestamp: endTimestamp.toMillis(),
      });

      const q = query(
        collection(db, VISITORS_COLLECTION),
        where('sessionStartTime', '>=', startTimestamp),
        where('sessionStartTime', '<=', endTimestamp),
        orderBy('sessionStartTime', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const visitors = snapshot.docs.map(doc => this.docToVisitor(doc.id, doc.data()));
      
      console.log('[WebsiteAnalyticsService] Retrieved visitors:', {
        count: visitors.length,
        visitorIds: visitors.map(v => v.id).slice(0, 5), // Log first 5 IDs
      });
      
      return visitors;
    } catch (error: any) {
      console.error('[WebsiteAnalyticsService] Error fetching visitors in date range:', error);
      // If index error, try simpler query without orderBy
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.warn('[WebsiteAnalyticsService] Index missing, trying simpler query...');
        const q = query(
          collection(db, VISITORS_COLLECTION),
          where('sessionStartTime', '>=', Timestamp.fromDate(startDate)),
          where('sessionStartTime', '<=', Timestamp.fromDate(endDate))
        );
        const snapshot = await getDocs(q);
        const visitors = snapshot.docs.map(doc => this.docToVisitor(doc.id, doc.data()));
        // Sort in memory
        return visitors.sort((a, b) => b.sessionStartTime.getTime() - a.sessionStartTime.getTime());
      }
      throw error;
    }
  }

  // ========== Visitor Events ==========

  static async trackEvent(eventData: Partial<VisitorEvent>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      eventName: eventData.eventName || '',
      eventCategory: eventData.eventCategory || '',
      eventLabel: eventData.eventLabel || null,
      eventValue: eventData.eventValue || null,
      pageUrl: eventData.pageUrl || '',
      timestamp: eventData.timestamp ? Timestamp.fromDate(eventData.timestamp) : now,
      metadata: eventData.metadata || {},
    };

    const docRef = await addDoc(collection(db, VISITOR_EVENTS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Conversions ==========

  static async trackConversion(conversionData: Partial<VisitorConversion>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      conversionType: conversionData.conversionType || '',
      value: conversionData.value || null,
      timestamp: conversionData.timestamp ? Timestamp.fromDate(conversionData.timestamp) : now,
      attributionData: conversionData.attributionData || null,
    };

    const docRef = await addDoc(collection(db, CONVERSIONS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Conversion Funnels ==========

  static async createFunnel(funnelData: Partial<ConversionFunnel>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: funnelData.name || '',
      description: funnelData.description || null,
      steps: funnelData.steps || [],
      createdAt: now,
      isActive: funnelData.isActive ?? true,
    };

    const docRef = await addDoc(collection(db, FUNNELS_COLLECTION), data);
    return docRef.id;
  }

  static async updateFunnel(funnelId: string, funnelData: Partial<ConversionFunnel>): Promise<void> {
    const data: any = {
      name: funnelData.name || '',
      description: funnelData.description || null,
      steps: funnelData.steps || [],
      isActive: funnelData.isActive ?? true,
      lastUpdated: Timestamp.now(),
    };

    await updateDoc(doc(db, FUNNELS_COLLECTION, funnelId), data);
  }

  static async getAllFunnels(): Promise<ConversionFunnel[]> {
    const q = query(
      collection(db, FUNNELS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToFunnel(doc.id, doc.data()));
  }

  // ========== Analytics Summary ==========

  static async getAnalyticsSummary(startDate: Date, endDate: Date): Promise<WebsiteAnalyticsSummary> {
    console.log('[WebsiteAnalyticsService] Generating analytics summary:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    
    const visitors = await this.getVisitorsInDateRange(startDate, endDate);
    
    console.log('[WebsiteAnalyticsService] Visitors retrieved for summary:', {
      count: visitors.length,
    });
    
    // Calculate metrics
    const totalVisitors = visitors.length;
    const newVisitors = visitors.filter(v => v.isNewVisitor).length;
    const returningVisitors = visitors.filter(v => v.isReturning).length;
    const uniqueVisitors = new Set(visitors.map(v => v.sessionId)).size;
    const totalSessions = visitors.length;
    
    const avgSessionDuration = visitors.length > 0
      ? visitors.reduce((sum, v) => sum + v.timeOnSite, 0) / visitors.length
      : 0;
    
    const bouncedSessions = visitors.filter(v => v.pagesViewed.length === 1 && v.timeOnSite < 30).length;
    const bounceRate = totalSessions > 0 ? (bouncedSessions / totalSessions) * 100 : 0;
    
    const pagesPerSession = visitors.length > 0
      ? visitors.reduce((sum, v) => sum + v.pagesViewed.length, 0) / visitors.length
      : 0;
    
    // Traffic sources
    const sourceMap = new Map<string, { source: string; medium: string; visitors: number; sessions: number; conversions: number }>();
    visitors.forEach(v => {
      const key = `${v.source}-${v.medium || 'unknown'}`;
      const existing = sourceMap.get(key) || { source: v.source, medium: v.medium || 'unknown', visitors: 0, sessions: 0, conversions: 0 };
      existing.visitors++;
      existing.sessions++;
      if (v.convertedAt) existing.conversions++;
      sourceMap.set(key, existing);
    });
    
    const trafficSources = Array.from(sourceMap.values()).map(s => ({
      ...s,
      conversionRate: s.visitors > 0 ? (s.conversions / s.visitors) * 100 : 0,
    }));
    
    // Top pages
    const pageMap = new Map<string, { pageUrl: string; views: number; uniqueViews: Set<string>; timeOnPage: number[]; bounces: number; conversions: number }>();
    visitors.forEach(v => {
      v.pagesViewed.forEach(page => {
        const existing = pageMap.get(page) || {
          pageUrl: page,
          views: 0,
          uniqueViews: new Set(),
          timeOnPage: [],
          bounces: 0,
          conversions: 0,
        };
        existing.views++;
        existing.uniqueViews.add(v.sessionId);
        if (v.timeOnSite > 0) existing.timeOnPage.push(v.timeOnSite);
        if (v.pagesViewed.length === 1 && v.timeOnSite < 30) existing.bounces++;
        if (v.convertedAt && page === v.entryPage) existing.conversions++;
        pageMap.set(page, existing);
      });
    });
    
    const topPages = Array.from(pageMap.entries())
      .map(([url, data]) => ({
        pageUrl: url,
        views: data.views,
        uniqueViews: data.uniqueViews.size,
        avgTimeOnPage: data.timeOnPage.length > 0
          ? data.timeOnPage.reduce((sum, t) => sum + t, 0) / data.timeOnPage.length
          : 0,
        bounceRate: data.views > 0 ? (data.bounces / data.views) * 100 : 0,
        conversions: data.conversions,
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);
    
    // Device breakdown
    const deviceBreakdown = {
      desktop: visitors.filter(v => v.deviceType === 'desktop').length,
      mobile: visitors.filter(v => v.deviceType === 'mobile').length,
      tablet: visitors.filter(v => v.deviceType === 'tablet').length,
    };
    
    // Geographic data
    const geoMap = new Map<string, { country: string; region?: string; visitors: number; conversions: number }>();
    visitors.forEach(v => {
      if (v.country) {
        const key = v.region ? `${v.country}-${v.region}` : v.country;
        const existing = geoMap.get(key) || { country: v.country, region: v.region, visitors: 0, conversions: 0 };
        existing.visitors++;
        if (v.convertedAt) existing.conversions++;
        geoMap.set(key, existing);
      }
    });
    
    const geographicData = Array.from(geoMap.values());
    
    // Conversions
    const totalConversions = visitors.filter(v => v.convertedAt).length;
    const conversionRate = totalVisitors > 0 ? (totalConversions / totalVisitors) * 100 : 0;
    const conversionsByType: Record<string, number> = {};
    visitors.forEach(v => {
      if (v.conversionType) {
        conversionsByType[v.conversionType] = (conversionsByType[v.conversionType] || 0) + 1;
      }
    });
    const conversionValue = visitors
      .filter(v => v.convertedValue)
      .reduce((sum, v) => sum + (v.convertedValue || 0), 0);
    
    // Lead qualification
    const potentialEmployers = visitors.filter(v => v.isPotentialEmployer).length;
    const potentialProviders = visitors.filter(v => v.isPotentialProvider).length;
    const highInterestVisitors = visitors.filter(v => v.interestLevel === 'high').length;
    
    // Daily trends (simplified - in production, you'd want more sophisticated aggregation)
    const dailyMap = new Map<string, { date: Date; visitors: number; sessions: number; conversions: number }>();
    visitors.forEach(v => {
      const dateKey = v.sessionStartTime.toISOString().split('T')[0];
      const existing = dailyMap.get(dateKey) || {
        date: new Date(dateKey),
        visitors: 0,
        sessions: 0,
        conversions: 0,
      };
      existing.visitors++;
      existing.sessions++;
      if (v.convertedAt) existing.conversions++;
      dailyMap.set(dateKey, existing);
    });
    
    const dailyVisitors = Array.from(dailyMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    
    return {
      period: { start: startDate, end: endDate },
      totalVisitors,
      newVisitors,
      returningVisitors,
      uniqueVisitors,
      totalSessions,
      averageSessionDuration: avgSessionDuration,
      bounceRate,
      pagesPerSession,
      trafficSources,
      topPages,
      deviceBreakdown,
      geographicData,
      totalConversions,
      conversionRate,
      conversionsByType,
      conversionValue,
      potentialEmployers,
      potentialProviders,
      highInterestVisitors,
      dailyVisitors,
    };
  }

  // ========== Helper Methods ==========

  private static generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private static docToVisitor(id: string, data: any): WebsiteVisitor {
    return {
      id,
      sessionId: data.sessionId || '',
      userId: data.userId,
      isNewVisitor: data.isNewVisitor ?? true,
      isReturning: data.isReturning ?? false,
      deviceType: data.deviceType || 'desktop',
      browser: data.browser || 'unknown',
      os: data.os || 'unknown',
      screenResolution: data.screenResolution,
      language: data.language || 'en',
      country: data.country,
      region: data.region,
      city: data.city,
      referrer: data.referrer,
      source: data.source || 'direct',
      medium: data.medium,
      campaign: data.campaign,
      term: data.term,
      adGroup: data.adGroup,
      keyword: data.keyword,
      utmSource: data.utmSource,
      utmMedium: data.utmMedium,
      utmCampaign: data.utmCampaign,
      utmTerm: data.utmTerm,
      utmContent: data.utmContent,
      gclid: data.gclid,
      gclsrc: data.gclsrc,
      entryPage: data.entryPage || '/',
      exitPage: data.exitPage,
      pagesViewed: data.pagesViewed || [],
      timeOnSite: data.timeOnSite || 0,
      sessionStartTime: data.sessionStartTime?.toDate() || new Date(),
      sessionEndTime: data.sessionEndTime?.toDate(),
      isActive: data.isActive ?? true,
      scrollDepth: data.scrollDepth,
      eventsTriggered: data.eventsTriggered || [],
      conversions: data.conversions || [],
      isPotentialEmployer: data.isPotentialEmployer ?? false,
      isPotentialProvider: data.isPotentialProvider ?? false,
      isPotentialEmployee: data.isPotentialEmployee ?? false,
      interestLevel: data.interestLevel,
      interestSignals: data.interestSignals || [],
      convertedAt: data.convertedAt?.toDate(),
      conversionType: data.conversionType,
      convertedValue: data.convertedValue,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastSeen: data.lastSeen?.toDate() || new Date(),
    };
  }

  private static docToFunnel(id: string, data: any): ConversionFunnel {
    return {
      id,
      name: data.name || '',
      description: data.description,
      steps: data.steps || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      isActive: data.isActive ?? true,
    };
  }
}

